/*
 * Snuggli UGC videos.
 *
 *  <custom-ugc-videos>  the "Don't just take our word for it" strip: every card runs
 *                       a muted, looping preview once the strip is near the viewport,
 *                       and tapping a card opens the fullscreen player — one video per
 *                       slide, swiped vertically, with a progress bar, a mute toggle,
 *                       close, prev/next arrows, and the next video starting when the
 *                       current one ends.
 *
 * The player is a port of the Dream Curls "Included Tabs" popup (custom-dci-tabs).
 * Embla is not used: this theme's galleries already move on CSS scroll snapping, and
 * the same approach here keeps the page free of a carousel library.
 *
 * Sources are plain files or HLS manifests. A manifest plays natively on Safari and
 * through hls.js elsewhere, which is fetched from cdnjs the first time it is needed —
 * the product template still points at Cloudflare Stream .m3u8 links.
 */

;(function () {
  'use strict'

  const HLS_LIBRARY = 'https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.6.5/hls.min.js'
  let hlsLoader = null

  function isHlsSource(src) {
    return /\.m3u8(\?|#|$)/i.test(src)
  }

  function loadHls() {
    if (window.Hls) return Promise.resolve(window.Hls)
    if (!hlsLoader) {
      hlsLoader = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = HLS_LIBRARY
        script.async = true
        script.onload = () => resolve(window.Hls)
        script.onerror = () => reject(new Error('hls.js did not load'))
        document.head.appendChild(script)
      })
    }
    return hlsLoader
  }

  // Puts a video's source on the element once, and resolves when it is safe to play.
  // Held in data-src until then so nothing downloads before it is looked at.
  function attachSource(video) {
    if (!video) return Promise.resolve()

    const src = video.dataset.src
    if (!src) return Promise.resolve()
    video.removeAttribute('data-src')

    if (!isHlsSource(src) || video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src
      return Promise.resolve()
    }

    return loadHls()
      .then((Hls) => {
        if (!Hls || !Hls.isSupported()) {
          video.src = src
          return
        }
        const hls = new Hls({ capLevelToPlayerSize: true })
        // A stream that cannot be fetched leaves the poster in place; the reason goes to
        // the console, since from the page it just looks like a video that never starts.
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data && data.fatal) console.warn('UGC video: could not load', src, data.details)
        })
        hls.loadSource(src)
        hls.attachMedia(video)
        video.customHls = hls
      })
      .catch(() => {
        video.src = src
      })
  }

  function tryPlay(video) {
    const attempt = video.play()
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {})
  }

  class CustomUgcVideos extends HTMLElement {
    connectedCallback() {
      if (this.ready) return
      this.ready = true

      this.previews = Array.from(this.querySelectorAll('[data-ugc-preview]'))
      this.popup = this.querySelector('[data-ugc-popup]')
      this.track = this.popup ? this.popup.querySelector('[data-ugc-track]') : null
      this.slides = this.popup ? Array.from(this.popup.querySelectorAll('[data-ugc-slide]')) : []
      this.isMuted = true
      this.currentIndex = 0

      this.previews.forEach((video) => {
        video.addEventListener('playing', () => video.classList.add('is-playing'))
      })
      this.watchPreviews()

      this.addEventListener('click', (event) => this.onClick(event))
      this.addEventListener('keydown', (event) => this.onKeydown(event))

      if (this.track) {
        this.track.addEventListener(
          'scroll',
          () => {
            window.clearTimeout(this.scrollTimer)
            this.scrollTimer = window.setTimeout(() => this.syncFromScroll(), 80)
          },
          { passive: true },
        )
      }
    }

    /* ------------------------------------------------------------- previews */

    // The previews start together the first time the strip comes within 400px of
    // the viewport, and never before: three streams are not worth fetching for a
    // section the shopper may not scroll to.
    watchPreviews() {
      if (!this.previews.length) return

      const start = () => this.previews.forEach((video) => this.playPreview(video))

      if (!('IntersectionObserver' in window)) {
        start()
        return
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return
          observer.disconnect()
          start()
        },
        { rootMargin: '400px 0px' },
      )

      observer.observe(this)
    }

    playPreview(video) {
      attachSource(video).then(() => {
        video.muted = true
        tryPlay(video)
      })
    }

    /* --------------------------------------------------------------- events */

    onClick(event) {
      const opener = event.target.closest('[data-ugc-open]')
      if (opener && this.contains(opener)) {
        event.preventDefault()
        this.open(parseInt(opener.dataset.ugcOpen, 10) || 0, opener)
        return
      }

      if (!this.popup || this.popup.hidden) return

      if (event.target.closest('[data-ugc-prev]')) {
        this.step(-1)
        return
      }

      if (event.target.closest('[data-ugc-next]')) {
        this.step(1)
        return
      }

      if (event.target.closest('[data-ugc-mute]')) {
        this.toggleMute()
        return
      }

      if (event.target.closest('[data-ugc-close]')) {
        this.close()
        return
      }

      // A tap on a neighbour peeking in from above or below brings it to the centre;
      // a tap on the video that is already playing does nothing.
      const video = event.target.closest('[data-ugc-video]')
      if (video) {
        const index = this.slides.findIndex((slide) => slide.contains(video))
        if (index !== -1 && index !== this.currentIndex) this.select(index)
        return
      }

      // Everything else inside the player is backdrop, and a lightbox's backdrop closes it.
      if (event.target.closest('[data-ugc-popup]')) this.close()
    }

    onKeydown(event) {
      if (!this.popup || this.popup.hidden) return

      if (event.key === 'Escape') {
        this.close()
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault()
        this.step(-1)
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault()
        this.step(1)
      }
    }

    /* --------------------------------------------------------------- player */

    open(index, trigger) {
      if (!this.popup || !this.slides.length || !this.track) return

      this.activeElement = trigger || document.activeElement
      this.popup.hidden = false
      // The same lock the drawers and the zoom viewer use; it also parks the sticky bar.
      document.body.classList.add('overflow-hidden')

      this.select(index)

      const closeButton = this.slides[this.currentIndex].querySelector('[data-ugc-close]')
      if (typeof trapFocus === 'function') trapFocus(this.popup, closeButton || this.track)
    }

    close() {
      if (!this.popup || this.popup.hidden) return

      this.stopProgress()

      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-ugc-video]')
        if (video) {
          video.pause()
          if (video.readyState > 0) video.currentTime = 0
        }
        const bar = slide.querySelector('[data-ugc-progress]')
        if (bar) bar.style.width = '0%'
      })

      this.popup.hidden = true
      document.body.classList.remove('overflow-hidden')

      this.currentVideo = null
      this.currentBar = null

      // Sound is opt-in on every open, as in the source popup.
      this.isMuted = true
      this.syncMuteButtons()

      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement)
    }

    // No `behavior: smooth`: an animated scroll fights the track's mandatory snap points
    // on some engines — same reason as the gallery and the zoom viewer.
    select(index) {
      const clamped = Math.min(Math.max(index, 0), this.slides.length - 1)
      const slide = this.slides[clamped]
      if (!slide) return

      const top = slide.offsetTop - (this.track.clientHeight - slide.offsetHeight) / 2
      this.track.scrollTo({ top: Math.max(0, top) })

      this.markActive(clamped)
      this.playActive()
    }

    step(direction) {
      this.select(this.currentIndex + direction)
    }

    syncFromScroll() {
      if (!this.popup || this.popup.hidden || !this.slides.length) return

      const centre = this.track.scrollTop + this.track.clientHeight / 2
      let closest = this.currentIndex
      let best = Infinity

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetTop + slide.offsetHeight / 2 - centre)
        if (distance < best) {
          best = distance
          closest = index
        }
      })

      if (closest !== this.currentIndex) {
        this.markActive(closest)
        this.playActive()
      }
    }

    markActive(index) {
      this.currentIndex = index
      this.slides.forEach((slide, position) => slide.classList.toggle('is-active', position === index))

      const prev = this.popup.querySelector('[data-ugc-prev]')
      const next = this.popup.querySelector('[data-ugc-next]')
      if (prev) prev.disabled = index <= 0
      if (next) next.disabled = index >= this.slides.length - 1
    }

    // Only the active slide plays, and only the active slide has a source attached:
    // the neighbours show their poster until they are swiped in.
    playActive() {
      this.stopProgress()

      this.slides.forEach((slide, position) => {
        const bar = slide.querySelector('[data-ugc-progress]')
        if (bar) bar.style.width = '0%'

        if (position === this.currentIndex) return
        const video = slide.querySelector('[data-ugc-video]')
        if (video) {
          video.pause()
          if (video.readyState > 0) video.currentTime = 0
        }
      })

      const slide = this.slides[this.currentIndex]
      const video = slide.querySelector('[data-ugc-video]')
      this.currentVideo = video
      this.currentBar = slide.querySelector('[data-ugc-progress]')
      if (!video) return

      attachSource(video).then(() => {
        // The shopper may have moved on or closed the player while the source loaded.
        if (this.currentVideo !== video || this.popup.hidden) return
        video.muted = this.isMuted
        tryPlay(video)
        this.trackProgress()
      })
    }

    trackProgress() {
      const step = () => {
        const video = this.currentVideo
        if (!video) return

        if (this.currentBar && Number.isFinite(video.duration) && video.duration > 0) {
          this.currentBar.style.width = `${(video.currentTime / video.duration) * 100}%`
        }

        if (video.ended) {
          // The next video takes over when this one finishes; the last one starts the set again.
          this.select(this.currentIndex < this.slides.length - 1 ? this.currentIndex + 1 : 0)
          return
        }

        this.frame = requestAnimationFrame(step)
      }

      step()
    }

    stopProgress() {
      if (this.frame) cancelAnimationFrame(this.frame)
      this.frame = null
    }

    toggleMute() {
      this.isMuted = !this.isMuted

      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-ugc-video]')
        if (video) video.muted = true
      })
      if (this.currentVideo) this.currentVideo.muted = this.isMuted

      this.syncMuteButtons()
    }

    syncMuteButtons() {
      if (!this.popup) return
      this.popup.querySelectorAll('[data-ugc-mute]').forEach((button) => {
        button.dataset.muted = String(this.isMuted)
        const label = this.isMuted ? button.dataset.labelUnmute : button.dataset.labelMute
        if (label) button.setAttribute('aria-label', label)
      })
    }
  }

  if (!customElements.get('custom-ugc-videos')) {
    customElements.define('custom-ugc-videos', CustomUgcVideos)
  }
})()
